/**
 * EventClubDetailPage
 *
 * Vista a pantalla completa del detalle de un club inscrito a un evento.
 * Pensada para que la asociación o el director puedan ver toda la información
 * del club (inscritos, check-in, pagos, documentos) sin estar limitados al
 * desplegable compacto de la pestaña "Clubes" del evento.
 *
 * Ruta: /events/:eventId/clubs/:clubId
 */
import { useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  CheckCircle2,
  Clock,
  ShieldCheck,
  AlertCircle,
  DollarSign,
  FileText,
  Image as ImageIcon,
  Search,
  Download,
  RefreshCw,
  UserPlus,
  Pencil,
  CheckCircle,
  XCircle,
  Loader2,
  Banknote,
} from "lucide-react";
import toast from "react-hot-toast";
import { syncClubConsentsFromProfile } from "@/lib/services/eventConsents";
import { useAuthStore } from "@/stores/authStore";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import {
  useEvent,
  useEventRegistrations,
  useEventPayments,
  useUpdateRegistrationStatus,
} from "@/hooks/useEvents";
import { useEventConsents } from "@/hooks/useEventConsents";
import { useClubs } from "@/hooks/useOrganization";
import ConsentStatusBadge from "@/components/consent/ConsentStatusBadge";
import MemberConsentDocsModal from "@/components/consent/MemberConsentDocsModal";
import EventClubAttachmentsPanel from "@/components/events/EventClubAttachmentsPanel";
import EventRegistrationModal from "./components/EventRegistrationModal";
import AdminRegistrationEditModal from "./components/AdminRegistrationEditModal";
import SwapRegistrationModal from "./components/SwapRegistrationModal";
import CashPaymentModal from "./components/CashPaymentModal";
import Modal, { ModalFooter } from "@/components/ui/Modal";
import { useConfirm } from "@/components/ui/ConfirmDialog";
import { usePermissions } from "@/hooks/usePermissions";
import { Can } from "@/components/auth/Can";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { formatCurrency } from "@/lib/utils";
import { exportToXlsx } from "@/lib/utils/exportXlsx";
import { buildPersonsMap, personColumns } from "@/lib/utils/eventExport";
import { usePersons } from "@/hooks/usePersons";
import type { EventConsent, EventRegistration } from "@/types";

type StatusFilter = "all" | "pending" | "approved" | "cancelled" | "rejected";
type CheckinFilter = "all" | "completed" | "in_progress" | "not_started";

export default function EventClubDetailPage() {
  const { eventId, clubId } = useParams<{
    eventId: string;
    clubId: string;
  }>();
  const navigate = useNavigate();

  const { data: event, isLoading: loadingEvent } = useEvent(eventId);
  const { data: registrations, isLoading: loadingRegs } =
    useEventRegistrations(eventId);
  const { data: eventPayments } = useEventPayments(eventId);
  const { data: persons } = usePersons();
  const {
    byMemberId,
    progressFor,
    loading: loadingConsents,
  } = useEventConsents(eventId, event?.consentChecklistConfig);
  const { data: allClubs } = useClubs();
  const { role } = usePermissions();
  const { data: myAccessibleClubs } = useAccessibleClubs();
  const myClubId = myAccessibleClubs?.[0]?.id;
  const isAdmin = role === "admin_association" || role === "admin_union";
  const isOrganizer =
    event?.organizerId === myClubId ||
    (role === "admin_association" && event?.level === "association") ||
    (role === "admin_union" && event?.level === "union");
  const confirm = useConfirm();
  const updateRegistrationStatus = useUpdateRegistrationStatus();
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [adminEditReg, setAdminEditReg] = useState<EventRegistration | null>(
    null,
  );
  const [swapReg, setSwapReg] = useState<EventRegistration | null>(null);
  const [rejectRegistrationId, setRejectRegistrationId] = useState<
    string | null
  >(null);
  const [rejectReasonDraft, setRejectReasonDraft] = useState("");
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  const handleApproveRegistration = async (registrationId: string) => {
    if (!event) return;
    try {
      await updateRegistrationStatus.mutateAsync({
        id: registrationId,
        status: "approved",
        eventId: event.id,
      });
      toast.success("Inscripción aprobada");
    } catch {
      toast.error("Error al aprobar");
    }
  };

  const submitRejectRegistration = async () => {
    if (!event || !rejectRegistrationId) return;
    try {
      await updateRegistrationStatus.mutateAsync({
        id: rejectRegistrationId,
        status: "rejected",
        eventId: event.id,
        rejectionReason: rejectReasonDraft.trim() || undefined,
      });
      toast.success("Inscripción rechazada");
    } catch {
      toast.error("Error al rechazar");
    }
    setRejectRegistrationId(null);
    setRejectReasonDraft("");
  };

  const handleApproveAllClubPending = async () => {
    if (!event || !registrations || !clubId) return;
    const pending = registrations.filter(
      (r) => r.clubId === clubId && r.status === "pending",
    );
    if (pending.length === 0) return;
    const confirmed = await confirm({
      title: "Aprobar pendientes del club",
      description: `¿Aprobar ${pending.length} inscripción(es) pendiente(s) de este club?`,
      variant: "info",
      confirmText: "Aprobar todas",
    });
    if (!confirmed) return;
    setIsBulkProcessing(true);
    let ok = 0;
    let fail = 0;
    for (const r of pending) {
      try {
        await updateRegistrationStatus.mutateAsync({
          id: r.id,
          status: "approved",
          eventId: event.id,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    setIsBulkProcessing(false);
    if (fail === 0) toast.success(`${ok} inscripción(es) aprobada(s)`);
    else toast.error(`${ok} ok, ${fail} con error`);
  };

  const club = useMemo(
    () => (allClubs ?? []).find((c) => c.id === clubId),
    [allClubs, clubId],
  );

  const clubRegs = useMemo<EventRegistration[]>(
    () =>
      (registrations ?? []).filter(
        (r) =>
          r.clubId === clubId && !["cancelled", "rejected"].includes(r.status),
      ),
    [registrations, clubId],
  );

  const allClubRegs = useMemo<EventRegistration[]>(
    () => (registrations ?? []).filter((r) => r.clubId === clubId),
    [registrations, clubId],
  );

  const clubPayments = useMemo(
    () => (eventPayments ?? []).filter((p) => p.clubId === clubId),
    [eventPayments, clubId],
  );

  // Métricas
  const stats = useMemo(() => {
    let approved = 0;
    let pending = 0;
    let consentCompleted = 0;
    let consentInProgress = 0;
    let consentNotStarted = 0;
    let amount = 0; // total a cobrar (solo aprobados)

    clubRegs.forEach((r) => {
      if (r.status === "approved") {
        approved++;
        amount += r.unitPrice ?? 0;
      }
      if (r.status === "pending") pending++;
      const prog = progressFor(r.memberId);
      if (prog.completed) consentCompleted++;
      else if (prog.doneCount > 0) consentInProgress++;
      else consentNotStarted++;
    });

    const paidAmount = clubPayments
      .filter((p) => p.status === "approved")
      .reduce((acc, p) => acc + p.amount, 0);
    const pendingAmount = clubPayments
      .filter((p) => p.status === "pending")
      .reduce((acc, p) => acc + p.amount, 0);

    return {
      total: clubRegs.length,
      approved,
      pending,
      consentCompleted,
      consentInProgress,
      consentNotStarted,
      amount,
      paidAmount,
      pendingAmount,
    };
  }, [clubRegs, clubPayments, progressFor]);

  // Distribución por categoría
  const byCategory = useMemo(() => {
    const map = new Map<string, number>();
    clubRegs.forEach((r) => {
      map.set(r.category, (map.get(r.category) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);
  }, [clubRegs]);

  // Filtros tabla
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [checkinFilter, setCheckinFilter] = useState<CheckinFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [isSyncing, setIsSyncing] = useState(false);
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const handleSyncFromProfile = async () => {
    if (!eventId || !clubId) return;
    if (
      !window.confirm(
        "Esto creará o completará los consentimientos de todos los inscritos del club usando los documentos cargados en cada perfil. ¿Continuar?",
      )
    )
      return;
    setIsSyncing(true);
    const t = toast.loading("Sincronizando documentos del perfil...");
    try {
      const res = await syncClubConsentsFromProfile({
        eventId,
        clubId,
        userId: user?.uid,
      });
      toast.success(
        `Sincronización lista: ${res.created} creados, ${res.updated} actualizados, ${res.skipped} sin cambios${res.errors ? `, ${res.errors} errores` : ""}`,
        { id: t, duration: 6000 },
      );
      await queryClient.invalidateQueries({
        queryKey: ["eventConsents", eventId],
      });
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : "Error al sincronizar", {
        id: t,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const visibleRegs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allClubRegs
      .filter((r) => {
        if (statusFilter === "all") {
          if (r.status === "cancelled" || r.status === "rejected") return false;
        } else if (r.status !== statusFilter) {
          return false;
        }
        if (categoryFilter !== "all" && r.category !== categoryFilter)
          return false;
        if (checkinFilter !== "all") {
          const prog = progressFor(r.memberId);
          if (checkinFilter === "completed" && !prog.completed) return false;
          if (
            checkinFilter === "in_progress" &&
            !(prog.doneCount > 0 && !prog.completed)
          )
            return false;
          if (checkinFilter === "not_started" && prog.doneCount > 0)
            return false;
        }
        if (term && !r.personName?.toLowerCase().includes(term)) return false;
        return true;
      })
      .sort((a, b) =>
        (a.personName ?? "").localeCompare(b.personName ?? "", "es"),
      );
  }, [
    allClubRegs,
    statusFilter,
    categoryFilter,
    checkinFilter,
    search,
    progressFor,
  ]);

  const [docsModal, setDocsModal] = useState<{
    consent: EventConsent | null;
    memberName: string;
  } | null>(null);

  const consentPct =
    stats.total > 0
      ? Math.round((stats.consentCompleted / stats.total) * 100)
      : 0;
  const paidPct =
    stats.amount > 0 ? Math.round((stats.paidAmount / stats.amount) * 100) : 0;

  const handleExportCsv = () => {
    if (!event) return;
    const personsMap = buildPersonsMap(persons);
    exportToXlsx({
      filename: `${event.name}_${club?.name ?? "club"}_inscritos`,
      sheetName: "Inscritos",
      rows: visibleRegs,
      columns: [
        { header: "Nombre", key: (r) => r.personName ?? "", width: 35 },
        ...personColumns(personsMap),
        { header: "Categoria", key: (r) => r.category, width: 18 },
        { header: "Estado", key: (r) => r.status, width: 14 },
        {
          header: "Check-in",
          key: (r) => {
            const prog = progressFor(r.memberId);
            return prog.completed
              ? "completo"
              : prog.doneCount > 0
                ? `${prog.doneCount}/${prog.totalCount}`
                : "sin iniciar";
          },
          width: 14,
        },
        {
          header: "Documentos",
          key: (r) => progressFor(r.memberId).totalCount,
          width: 12,
        },
        { header: "Precio", key: (r) => r.unitPrice ?? 0, width: 12 },
      ],
    });
  };

  if (loadingEvent || loadingRegs) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-500">Cargando…</p>
      </div>
    );
  }

  if (!event || !eventId || !clubId) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Evento o club no encontrado.</p>
        <Button variant="outline" className="mt-3" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </div>
    );
  }

  const clubName = club?.name ?? clubRegs[0]?.clubName ?? "Club";

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
      {/* Breadcrumb / back */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Link
          to={`/events/${eventId}`}
          className="inline-flex items-center gap-1 hover:text-purple-600"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al evento
        </Link>
        <span>·</span>
        <span className="truncate">{event.name}</span>
      </div>

      {/* Header del club */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardContent className="p-5 bg-gradient-to-br from-purple-50 to-white">
          <div className="flex items-start gap-4">
            {club?.logoUrl ? (
              <img
                src={club.logoUrl}
                alt={clubName}
                className="w-16 h-16 rounded-2xl object-cover border border-purple-200 bg-white shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center font-bold text-xl shrink-0">
                {clubName
                  .split(" ")
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                {clubName}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Inscripciones al evento{" "}
                <span className="font-medium text-gray-800">{event.name}</span>
              </p>
              {byCategory.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {byCategory.map((c) => (
                    <span
                      key={c.category}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-purple-200 text-purple-700"
                    >
                      {c.category} · {c.count}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {(isAdmin || isOrganizer) && (
              <div className="flex flex-col gap-2 shrink-0">
                <Can permission="events.write" clubId={clubId}>
                  <button
                    onClick={() => setShowRegisterModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                    title="Inscribir miembros de este club al evento"
                  >
                    <UserPlus className="w-4 h-4" />
                    Inscribir
                  </button>
                </Can>
                {isAdmin && (
                  <Can permission="finance.write">
                    <button
                      onClick={() => setShowCashModal(true)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                      title="Registrar pago en efectivo recibido del club"
                    >
                      <Banknote className="w-4 h-4" />
                      Pago efectivo
                    </button>
                  </Can>
                )}
                {isOrganizer && stats.pending > 0 && (
                  <Can permission="events.approve">
                    <button
                      onClick={handleApproveAllClubPending}
                      disabled={isBulkProcessing}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
                      title="Aprobar todas las inscripciones pendientes de este club"
                    >
                      {isBulkProcessing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Aprobar {stats.pending}
                    </button>
                  </Can>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          icon={<Users className="w-4 h-4" />}
          label="Inscritos"
          value={stats.total.toString()}
          tone="blue"
          sub={stats.pending > 0 ? `${stats.pending} pendientes` : undefined}
        />
        <MetricCard
          icon={<CheckCircle2 className="w-4 h-4" />}
          label="Aprobados"
          value={`${stats.approved}/${stats.total}`}
          tone="green"
        />
        <MetricCard
          icon={<ShieldCheck className="w-4 h-4" />}
          label="Check-in completo"
          value={`${stats.consentCompleted}/${stats.total}`}
          sub={`${consentPct}%`}
          tone="emerald"
        />
        <MetricCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Recaudado"
          value={formatCurrency(stats.paidAmount)}
          sub={`de ${formatCurrency(stats.amount)} (${paidPct}%)`}
          tone="amber"
        />
      </div>

      {/* Progreso check-in y pagos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 font-medium flex items-center gap-1">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                Progreso de check-in
              </span>
              <span className="text-gray-500 text-xs">
                {stats.consentCompleted}/{stats.total} ({consentPct}%)
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  consentPct >= 80
                    ? "bg-green-500"
                    : consentPct >= 40
                      ? "bg-amber-500"
                      : "bg-red-400"
                }`}
                style={{ width: `${consentPct}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
              <Stat
                icon={<CheckCircle2 className="w-3 h-3 text-green-600" />}
                label="Completos"
                value={stats.consentCompleted}
              />
              <Stat
                icon={<Clock className="w-3 h-3 text-amber-600" />}
                label="En progreso"
                value={stats.consentInProgress}
              />
              <Stat
                icon={<AlertCircle className="w-3 h-3 text-gray-500" />}
                label="Sin iniciar"
                value={stats.consentNotStarted}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 font-medium flex items-center gap-1">
                <DollarSign className="w-4 h-4 text-emerald-600" />
                Pagos del club
              </span>
              <span className="text-gray-500 text-xs">
                {clubPayments.length}{" "}
                {clubPayments.length === 1 ? "pago" : "pagos"}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${paidPct}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
              <Stat
                label="Aprobado"
                value={formatCurrency(stats.paidAmount)}
                tone="text-emerald-700"
              />
              <Stat
                label="Pendiente"
                value={formatCurrency(stats.pendingAmount)}
                tone="text-amber-700"
              />
              <Stat
                label="Total esperado"
                value={formatCurrency(stats.amount)}
                tone="text-gray-700"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documentos anexos */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Documentos anexos del club
          </h2>
          <EventClubAttachmentsPanel
            event={{ id: event.id, name: event.name }}
            clubId={clubId}
            clubName={clubName}
            canEdit={false}
          />
        </CardContent>
      </Card>

      {/* Tabla de miembros */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Miembros ({visibleRegs.length}/{clubRegs.length})
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncFromProfile}
                disabled={isSyncing || allClubRegs.length === 0}
                title="Copia los documentos cargados en cada perfil hacia los consentimientos del evento"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-1 ${isSyncing ? "animate-spin" : ""}`}
                />
                {isSyncing ? "Sincronizando…" : "Sincronizar docs del perfil"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCsv}
                disabled={visibleRegs.length === 0}
              >
                <Download className="w-4 h-4 mr-1" />
                Exportar Excel
              </Button>
            </div>
          </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar miembro..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all w-full"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
            >
              <option value="all">Activas (sin cancelados/rechazados)</option>
              <option value="approved">Aprobados</option>
              <option value="pending">Pendientes</option>
              <option value="cancelled">Cancelados</option>
              <option value="rejected">Rechazados</option>
            </select>
            <select
              value={checkinFilter}
              onChange={(e) =>
                setCheckinFilter(e.target.value as CheckinFilter)
              }
              className="px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
            >
              <option value="all">Cualquier check-in</option>
              <option value="completed">Check-in completo</option>
              <option value="in_progress">En progreso</option>
              <option value="not_started">Sin iniciar</option>
            </select>
          </div>

          {byCategory.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setCategoryFilter("all")}
                className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                  categoryFilter === "all"
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-700 border-gray-200 hover:border-purple-300"
                }`}
              >
                Todas ({clubRegs.length})
              </button>
              {byCategory.map((c) => (
                <button
                  key={c.category}
                  onClick={() => setCategoryFilter(c.category)}
                  className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                    categoryFilter === c.category
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white text-gray-700 border-gray-200 hover:border-purple-300"
                  }`}
                >
                  {c.category} ({c.count})
                </button>
              ))}
            </div>
          )}

          {loadingConsents && (
            <p className="text-xs text-gray-400">
              Cargando estado de check-in…
            </p>
          )}

          {/* Tabla responsive */}
          <div className="overflow-x-auto -mx-4 md:mx-0">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Miembro</th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Categoría
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">Estado</th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Check-in
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Documentos
                  </th>
                  {(isAdmin || isOrganizer) && (
                    <th className="px-3 py-2 text-right font-semibold">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRegs.map((r) => {
                  const consent = byMemberId(r.memberId);
                  const prog = progressFor(r.memberId);
                  return (
                    <tr key={r.id} className="hover:bg-purple-50/40">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 text-white flex items-center justify-center font-bold text-[10px] shrink-0">
                            {r.personName
                              ?.split(" ")
                              .map((w) => w[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900 truncate">
                            {r.personName}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {r.category}
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-3 py-2">
                        <ConsentStatusBadge progress={prog} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() =>
                            setDocsModal({
                              consent,
                              memberName: r.personName,
                            })
                          }
                          disabled={!consent}
                          title={
                            consent ? "Ver documentos" : "Sin documentos aún"
                          }
                          className="inline-flex items-center gap-1 p-1.5 rounded-md hover:bg-purple-50 text-purple-600 disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                          {consent?.pdfDocument ? (
                            <FileText className="w-4 h-4" />
                          ) : (
                            <ImageIcon className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      {(isAdmin || isOrganizer) && (
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            {isOrganizer && r.status === "pending" && (
                              <>
                                <button
                                  onClick={() =>
                                    handleApproveRegistration(r.id)
                                  }
                                  disabled={
                                    updateRegistrationStatus.isPending ||
                                    isBulkProcessing
                                  }
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50"
                                  title="Aprobar"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                  Aprobar
                                </button>
                                <button
                                  onClick={() => {
                                    setRejectRegistrationId(r.id);
                                    setRejectReasonDraft("");
                                  }}
                                  disabled={
                                    updateRegistrationStatus.isPending ||
                                    isBulkProcessing
                                  }
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-md border border-red-200 transition-colors disabled:opacity-50"
                                  title="Rechazar"
                                >
                                  <XCircle className="w-3 h-3" />
                                  Rechazar
                                </button>
                              </>
                            )}
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => setAdminEditReg(r)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50 rounded-md border border-purple-200"
                                title="Editar inscripción (admin)"
                              >
                                <Pencil className="w-3 h-3" />
                                Editar
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {visibleRegs.length === 0 && (
                  <tr>
                    <td
                      colSpan={isAdmin || isOrganizer ? 6 : 5}
                      className="px-3 py-6 text-center text-sm text-gray-500"
                    >
                      No hay miembros que coincidan con los filtros.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {docsModal && (
        <MemberConsentDocsModal
          isOpen
          onClose={() => setDocsModal(null)}
          consent={docsModal.consent}
          memberName={docsModal.memberName}
        />
      )}

      {/* Inscripción manual desde el detalle del club.
          El modal pasa skipDateChecks: isAdmin → los admins pueden inscribir
          aunque las inscripciones ya estén cerradas por fecha. */}
      {showRegisterModal && (
        <EventRegistrationModal
          isOpen={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          event={event}
          clubId={clubId}
          clubName={clubName}
          clubChurchId={club?.churchId}
          registeredPersonIds={(registrations ?? [])
            .filter(
              (r) =>
                r.clubId === clubId &&
                !["rejected", "cancelled"].includes(r.status),
            )
            .map((r) => r.personId)}
        />
      )}

      {/* Edición avanzada de inscripción (admin) */}
      <AdminRegistrationEditModal
        isOpen={adminEditReg !== null}
        onClose={() => setAdminEditReg(null)}
        registration={adminEditReg}
        onSwap={(r) => setSwapReg(r)}
      />

      {/* Registrar pago en efectivo (admin) */}
      {showCashModal && (
        <CashPaymentModal
          isOpen={showCashModal}
          onClose={() => setShowCashModal(false)}
          event={event}
          clubId={clubId}
          clubName={clubName}
          expectedAmount={stats.amount}
          paidAmount={stats.paidAmount}
        />
      )}

      {/* Intercambiar integrante (admin / organizador) */}
      {event && swapReg && (
        <SwapRegistrationModal
          isOpen={true}
          onClose={() => setSwapReg(null)}
          event={event}
          outgoingRegistration={swapReg}
          clubName={swapReg.clubName ?? clubName}
          associationName={event.organizerName}
        />
      )}

      {/* Rechazar inscripción con motivo */}
      <Modal
        isOpen={rejectRegistrationId !== null}
        onClose={() => setRejectRegistrationId(null)}
        size="sm"
        title="Rechazar inscripción"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Esta acción marca la inscripción como rechazada. El director del
            club verá el estado y el motivo en su panel "Mis Inscritos".
          </p>
          <div>
            <label
              htmlFor="reject-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Motivo del rechazo (opcional)
            </label>
            <textarea
              id="reject-reason"
              value={rejectReasonDraft}
              onChange={(e) => setRejectReasonDraft(e.target.value)}
              rows={3}
              placeholder="Ej: cupo excedido, persona no cumple requisitos…"
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 text-sm transition-all"
            />
          </div>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => setRejectRegistrationId(null)}
              disabled={updateRegistrationStatus.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={submitRejectRegistration}
              disabled={updateRegistrationStatus.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {updateRegistrationStatus.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-1" />
              )}
              Rechazar inscripción
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone: "purple" | "blue" | "green" | "emerald" | "amber";
}

function MetricCard({ icon, label, value, sub, tone }: MetricCardProps) {
  const tones: Record<MetricCardProps["tone"], string> = {
    purple: "from-purple-500 to-purple-600",
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    emerald: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
  };
  return (
    <Card className="border-0 shadow-md overflow-hidden">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <span
            className={`w-6 h-6 rounded-md bg-gradient-to-br ${tones[tone]} text-white flex items-center justify-center`}
          >
            {icon}
          </span>
          {label}
        </div>
        <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
        {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function Stat({
  icon,
  label,
  value,
  tone = "text-gray-800",
}: {
  icon?: React.ReactNode;
  label: string;
  value: number | string;
  tone?: string;
}) {
  return (
    <div className="bg-gray-50 rounded-md px-2 py-1.5">
      <div className="flex items-center gap-1 text-[10px] text-gray-500 uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <p className={`text-sm font-semibold ${tone}`}>{value}</p>
    </div>
  );
}

function StatusPill({ status }: { status: EventRegistration["status"] }) {
  const map: Record<
    EventRegistration["status"],
    { label: string; cls: string }
  > = {
    pending: {
      label: "Pendiente",
      cls: "bg-amber-100 text-amber-700 border-amber-200",
    },
    approved: {
      label: "Aprobado",
      cls: "bg-emerald-100 text-emerald-700 border-emerald-200",
    },
    rejected: {
      label: "Rechazado",
      cls: "bg-red-100 text-red-700 border-red-200",
    },
    cancelled: {
      label: "Cancelado",
      cls: "bg-gray-100 text-gray-600 border-gray-200",
    },
    waitlist: {
      label: "Lista de espera",
      cls: "bg-blue-100 text-blue-700 border-blue-200",
    },
  };
  const v = map[status] ?? { label: status, cls: "bg-gray-100 text-gray-700" };
  return (
    <span
      className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border ${v.cls}`}
    >
      {v.label}
    </span>
  );
}
